/**
 * Feature E03-F06-T03 — "Market salary rate" search button (UI).
 *
 * 1:1 port of `../EER app/public/assets/js/project-kickoff.js`:
 *   - the inline `magnifying-glass-dollar` button rendered ONLY next to the
 *     salary question (`isSalaryField`, ~163);
 *   - the `searchSalaryRate(questionIdx)` handler (~529-579): show a spinner on
 *     the button, collect the search context, POST to the salary AI sub-flow,
 *     write `res.salary_rate` into the question answer (auto-resize + autosave),
 *     toast "Market salary rate found" / "No salary data found".
 *
 * Feature-owned, drop-in: the kick-off questionnaire view (E02-F05, built
 * elsewhere) renders this button next to the salary `<textarea>` and passes the
 * current question list + the two persistence callbacks (`onResult` to write the
 * answer + `onSaved` to fire `_saveKickoffQuestionsLocally` + `_autoSaveKickoff`).
 * The button is gated by {@link isSalaryQuestion} at the call site, but it also
 * renders nothing for a non-salary question as a safety net.
 *
 * All non-render rules live in `./salaryRateModel.ts` (collect params / parse the
 * result) and `./kickoffApi.ts` (`useSearchSalaryRate`); this is the thin React
 * shell. Accessible: a real `<button>` with an `aria-label`, keyboard-activatable,
 * `disabled` while the search is in flight (the spinner replaces the icon).
 */
import { useCallback } from 'react';
import { isSalaryQuestion } from './kickoffModel.js';
import { useSearchSalaryRate } from './kickoffApi.js';
import {
  collectSalarySearchParams,
  parseSalaryResult,
  SALARY_TOAST,
  type SalaryContextQuestion,
} from './salaryRateModel.js';
import { useToast } from '../ui/ToastContext.js';
import { ApiResultError } from '../api/client.js';

/** Visible button text (PM rework 2026-07-10 — an icon alone was not discoverable). */
export const SALARY_BUTTON_LABEL = {
  idle: 'Find market rate',
  busy: 'Searching…',
} as const;

export interface SalaryRateButtonProps {
  /** Project id (route target POST /projects/:id/ai/search_salary_rate). */
  readonly projectId: number;
  /** Flat index of the salary question in the list (so the answer maps back). */
  readonly questionIdx: number;
  /** The salary question (used to gate rendering on `isSalaryQuestion`). */
  readonly question: { readonly text?: string };
  /** The full questionnaire list — the search context is collected from it. */
  readonly questions: readonly SalaryContextQuestion[];
  /** Project `role_name` — the primary roleName source (legacy ~544). */
  readonly projectRoleName?: string | null;
  /**
   * Write the found salary string into the answer at `questionIdx`
   * (`questions[idx].answer = salaryInfo` + textarea auto-resize). The host owns
   * the question state; this callback updates it.
   */
  readonly onResult: (questionIdx: number, salaryRate: string) => void;
  /**
   * Persist after a successful write (`_saveKickoffQuestionsLocally` +
   * `_autoSaveKickoff`). Optional — omitted when the host autosaves on its own.
   */
  readonly onSaved?: () => void;
}

/**
 * The per-question market-rate search button. Renders nothing unless the question
 * is the salary field. While the mutation is pending the icon becomes a spinner
 * and the button is disabled (parity with `btn.disabled = true`).
 */
export function SalaryRateButton(props: SalaryRateButtonProps): React.JSX.Element | null {
  const { projectId, questionIdx, question, questions, projectRoleName, onResult, onSaved } = props;
  const toast = useToast();
  const mutation = useSearchSalaryRate(projectId);

  const onClick = useCallback(() => {
    if (mutation.isPending) return;
    const searchParams = collectSalarySearchParams(questions, projectRoleName);
    mutation.mutate(
      { questionIdx, searchParams },
      {
        onSuccess: (res) => {
          const parsed = parseSalaryResult(res);
          if (parsed.ok) {
            onResult(questionIdx, parsed.salaryRate);
            onSaved?.();
            toast.success(SALARY_TOAST.found);
          } else {
            toast.error(parsed.error ?? SALARY_TOAST.none);
          }
        },
        // E12-F06-T04 — `onError` is now REAL failures only (auth, network, 5xx,
        // a body that fails the schema). The honest "no salary data" answer is a
        // 200 `{ found:false, code, message }` and lands in `onSuccess` above:
        // before T04 it wore the legacy `{ success:false, error }` envelope, which
        // `toApiResult` demoted to an ApiResult error (`isLegacyErrorPayload`), so
        // every consumer had to dig the wording out of `detail.message` here.
        // Only an `ApiResultError` carries a message meant for a human; a thrown
        // ZodError (schema drift) must NEVER be toasted raw — hence the explicit
        // instanceof instead of the generic `errorMessage()` extractor, which
        // would happily surface `error.message` (a JSON dump) for a ZodError.
        onError: (error) => {
          const message =
            error instanceof ApiResultError && error.detail.message
              ? error.detail.message
              : SALARY_TOAST.error;
          toast.error(message);
        },
      },
    );
  }, [mutation, questions, projectRoleName, questionIdx, onResult, onSaved, toast]);

  // Safety net: only the salary question gets the button (legacy `isSalaryField`).
  if (!isSalaryQuestion(question)) return null;

  const busy = mutation.isPending;
  return (
    // PK-QA-17 — larger, higher-contrast target that reads as a real action (was a
    // near-invisible 11px `text-blue-400` icon). Styled off the design.md
    // `button-secondary` family (surface bg / primary text / border) + a hover fill
    // so it matches the neighbouring bordered icon-buttons instead of a raw blue
    // tint. `tabIndex={-1}` removed — the button MUST be reachable by keyboard Tab.
    // E03-F06-T03 rework (PM 2026-07-10): an icon-only magnifier was not
    // recognisable as an action — the button now carries a visible text label
    // ("Find market rate" / "Searching…" while in flight) next to the icon.
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="salary-search-button"
      aria-label="AI search market salary"
      title="AI search market salary"
      className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded border border-border px-3 py-1.5 text-sm font-medium text-primary hover:bg-surface-muted hover:text-primary-hover disabled:opacity-60"
    >
      {busy ? (
        <i
          className="fa-solid fa-spinner fa-spin"
          data-testid="salary-search-spinner"
          aria-hidden="true"
        />
      ) : (
        <i className="fa-solid fa-magnifying-glass-dollar" aria-hidden="true" />
      )}
      <span data-testid="salary-search-label">
        {busy ? SALARY_BUTTON_LABEL.busy : SALARY_BUTTON_LABEL.idle}
      </span>
    </button>
  );
}

export default SalaryRateButton;
